import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { Package, SignOut, User } from "@phosphor-icons/react";
import { Separator } from "../components/ui/separator";

export default function AccountPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user === false) { navigate("/auth"); return; }
    API.get("/orders")
      .then(r => setOrders(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (!user || user === false) return null;

  return (
    <div className="min-h-screen pt-28 pb-24 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="account-page">
      <div className="flex items-center justify-between mb-12">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3 font-medium">Your Account</p>
          <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[#FDFBF7] tracking-tighter">
            Welcome, {user.name}
          </h1>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 border border-white/10 px-5 py-2.5 text-xs tracking-[0.15em] uppercase text-[#A1A1AA] hover:text-[#FDFBF7] hover:border-white/20 transition-all duration-300 font-light"
          data-testid="logout-btn"
        >
          <SignOut size={14} weight="thin" /> Sign Out
        </button>
      </div>

      {/* Account info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
        <div className="bg-[#121212] border border-[rgba(212,175,55,0.1)] p-8">
          <User size={24} weight="thin" className="text-[#D4AF37] mb-4" />
          <p className="text-sm text-[#A1A1AA] font-light mb-1">Account</p>
          <p className="text-[#FDFBF7] font-light">{user.email}</p>
        </div>
        <div className="bg-[#121212] border border-[rgba(212,175,55,0.1)] p-8">
          <Package size={24} weight="thin" className="text-[#D4AF37] mb-4" />
          <p className="text-sm text-[#A1A1AA] font-light mb-1">Total Orders</p>
          <p className="text-[#FDFBF7] font-['Cormorant_Garamond'] text-2xl font-light">{orders.length}</p>
        </div>
        <div className="bg-[#121212] border border-[rgba(212,175,55,0.1)] p-8">
          <Link to="/wishlist" className="block">
            <p className="text-sm text-[#A1A1AA] font-light mb-1">Wishlist & Shopping</p>
            <p className="text-[#D4AF37] text-sm font-light hover:text-[#E5C158] transition-colors">View Wishlist &rarr;</p>
          </Link>
        </div>
      </div>

      {/* Orders */}
      <div>
        <h2 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#FDFBF7] mb-8">Order History</h2>
        {loading ? (
          <div className="space-y-4">
            {[1,2].map(i => <div key={i} className="h-24 bg-[#121212] animate-pulse border border-white/5" />)}
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16 bg-[#121212] border border-[rgba(212,175,55,0.1)]">
            <p className="text-[#A1A1AA] text-sm font-light mb-4">No orders yet</p>
            <Link to="/shop" className="text-[#D4AF37] text-sm font-light hover:text-[#E5C158] transition-colors" data-testid="account-shop-link">
              Start Shopping &rarr;
            </Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map(order => (
              <div key={order.id} className="bg-[#121212] border border-[rgba(212,175,55,0.1)] p-6" data-testid={`order-${order.id}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                  <div>
                    <p className="text-xs text-[#A1A1AA] font-light">Order #{order.id?.slice(0, 8)}</p>
                    <p className="text-xs text-[#A1A1AA] font-light mt-1">{new Date(order.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 text-[10px] uppercase tracking-[0.15em] border ${
                      order.status === "confirmed" ? "border-green-500/30 text-green-400" :
                      order.status === "shipped" ? "border-blue-500/30 text-blue-400" :
                      "border-[#D4AF37]/30 text-[#D4AF37]"
                    }`}>
                      {order.status}
                    </span>
                    <span className="text-[#D4AF37] font-['Cormorant_Garamond'] text-lg font-light">${order.total?.toFixed(2)}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  {order.items?.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 bg-[#0a0a0a] px-3 py-1.5">
                      <span className="text-xs text-[#FDFBF7] font-light">{item.name}</span>
                      <span className="text-xs text-[#A1A1AA] font-light">x{item.quantity}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
